import { ThemeColor } from '@/enums'
import { useFormContext, useFormItem } from '@/providers'
import { isUndefined } from '@/utils'
import { cloneElement, useCallback, useEffect, useState } from 'react'
import { Typography } from '../../atoms'
import { InfoTooltip } from '../InfoTooltip'
import { getError } from './helpers'
import { Container, HelperMessage, LabelContainer } from './styles'
import { OptionType } from './FormAutocompleteItem'
import { FormMonthSelectValue } from './FormMonthSelect'

interface Props {
  layout?: 'vertical' | 'horizontal'
  name: string
  label?: string
  initialValue?:
    | string
    | OptionType
    | Date
    | number
    | null
    | boolean
    | FormMonthSelectValue
  info?: string
  helperText?: string
  children: React.ReactElement
  ignoreErrorKeys?: string[]
  noMargin?: boolean
  darkInfo?: boolean
  showErrorHelperText?: boolean
  autoFocus?: boolean
}

export const FormItem = ({
  layout = 'vertical',
  name,
  label,
  initialValue = '',
  info,
  helperText,
  children,
  ignoreErrorKeys,
  noMargin,
  darkInfo = false,
  showErrorHelperText = true,
  autoFocus,
}: Props) => {
  const { setFieldValue, validateField } = useFormContext()
  const { value, onChange, error } = useFormItem(name)
  const [visited, setVisited] = useState(false)
  const errorMsg = getError(error, ignoreErrorKeys) as string | null

  const onChangeCallback = useCallback(
    async (value: any, shouldValidate = true) => {
      await onChange(value)
      if (shouldValidate) await validateField(name)
    },
    [onChange, validateField, name],
  )

  const clonedChildren = cloneElement(children, {
    // @ts-expect-error cloning a custom component
    value,
    className: getError(error, ignoreErrorKeys) && 'input-error-styles',
    error: Boolean(errorMsg),
    errorText: errorMsg,
    onChange: onChangeCallback,
    onFocus: !visited ? () => setVisited(true) : undefined,
    autoFocus,
  })

  useEffect(() => {
    if (isUndefined(value)) setFieldValue(name, initialValue)
  }, [])

  useEffect(() => {
    if (visited) {
      try {
        validateField(name)
      } catch (e) {
        console.log(e)
      }
    }
  }, [value])

  return (
    <Container layout={layout} noMargin={noMargin}>
      {(label || info) && (
        <LabelContainer layout={layout}>
          <Typography
            color={ThemeColor.b100}
            htmlFor={name}
            variant="inputLabel"
          >
            {label}
          </Typography>
          {info && (
            <InfoTooltip
              content={info}
              dark={darkInfo}
              iconColor={ThemeColor.b50}
              size={'menu'}
            />
          )}
        </LabelContainer>
      )}
      {clonedChildren}
      {showErrorHelperText && error && (
        <HelperMessage asChild color={ThemeColor.danger2}>
          {errorMsg}
        </HelperMessage>
      )}
      {helperText && !error && (
        <HelperMessage color={ThemeColor.b50}>{helperText}</HelperMessage>
      )}
    </Container>
  )
}
